import { useAuth, useClerk, useUser } from '@clerk/react';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { showWarnNotification } from '@vrplatform/theme';
import { type Maybe, day, isUUID, utc } from '@vrplatform/utils';
import { readLocalStorageValue } from '@mantine/hooks';
import {
  useCallback,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ApiClientProvider,
  useApiClient,
  useApiIsTeamIdFetching,
} from '../../api';
import { isLocalhost } from '../../env';
import { canAccessCustomOwnerPortal, useOwnerPortal } from '../../owner-portal';
import { useQueryClient } from '../../graphql';
import { useTrpcMutation, useTrpcQuery } from '../../trpc';
import { features as featureIds } from '../../utils';
import { isPlaywrightUserEmail } from '../isPlaywrightUserEmail';
import { OwnerPortalContactContextProvider } from '../owner';
import { captureSentryError } from '../sentry';
import { useExtension } from '../useExtension';
import { useIntercom } from '../useIntercom';
import {
  useProductAnalyticsPreference,
  useTrackingIdentify,
} from '../useTracking';
import {
  MeSessionContextProvider,
  teamIdContext,
  useTeamIdContext,
} from './team.context';
import {
  STORYBOOK_ME_QUERY_KEY,
  useLocalStorageTeamId,
  useMeQuery,
} from './team.queries';
import {
  createSeededMeQueryKey,
  getImpersonationActorTeamId,
  getPortalSwitchMessage,
  getRecentTeamIdsStorageKey,
  getSharedSessionTokenProvider,
  PORTAL_RELOAD_STORAGE_KEY,
  parseStoredRecentTeamIds,
  type PortalApp,
  readPortalSwitchLanding,
  readRoutedPortalApp,
  reconcileMeTeam,
  resolvePortalRouting,
  resolveSessionRequestedTeamId,
  resolveSessionTeamId,
  serializePortalTeamCookie,
  upsertRecentTeamId,
} from './team.session.utils';
import {
  getPartnerRegionStorageKey,
  resolvePartnerRegion,
} from './team.region.utils';
import type { DataRegion, UserRole } from './team.types';

type MeQueryResult = ReturnType<typeof useMeQuery>;

const isRegionScopedQuery = (query: {
  options?: { meta?: Record<string, unknown> };
}) => query.options?.meta?.regionScoped === true;

function usePartnerRegionSelection({
  me,
  userId,
}: {
  me: MeQueryResult;
  userId: string | undefined;
}) {
  const queryClient = useQueryClient();
  const team = me.data?.team;
  const managedTeamRegions =
    team?.type === 'partner'
      ? (me.data?.partnerContext?.managedTeamRegions ?? [])
      : [];
  const storageKey =
    userId && team?.id
      ? getPartnerRegionStorageKey({ teamId: team.id, userId })
      : null;
  const [sessionSelection, setSessionSelection] = useState<{
    dataRegion: DataRegion;
    storageKey: string;
  } | null>(null);
  const persistedDataRegion = storageKey
    ? sessionSelection?.storageKey === storageKey
      ? sessionSelection.dataRegion
      : readLocalStorageValue<DataRegion | null>({ key: storageKey })
    : null;
  const selectedRegion = resolvePartnerRegion({
    managedTeamRegions,
    persistedDataRegion,
  });

  useEffect(() => {
    if (!storageKey || !persistedDataRegion) return;
    if (
      managedTeamRegions.some(
        (region) => region.dataRegion === persistedDataRegion
      )
    )
      return;

    globalThis.localStorage?.removeItem(storageKey);
    if (sessionSelection?.storageKey === storageKey) {
      setSessionSelection(null);
    }
  }, [
    managedTeamRegions,
    persistedDataRegion,
    sessionSelection?.storageKey,
    storageKey,
  ]);

  const setSelectedDataRegion = useCallback(
    async (dataRegion: DataRegion) => {
      if (!storageKey) {
        throw new Error('Cannot select a partner region before /me resolves');
      }
      if (
        !managedTeamRegions.some(
          (managedRegion) => managedRegion.dataRegion === dataRegion
        )
      ) {
        throw new Error(`Cannot select unmanaged data region: ${dataRegion}`);
      }
      if (selectedRegion?.dataRegion === dataRegion) return;

      await queryClient.cancelQueries({
        type: 'active',
        predicate: isRegionScopedQuery,
      });
      queryClient.removeQueries({ predicate: isRegionScopedQuery });
      globalThis.localStorage?.setItem(storageKey, JSON.stringify(dataRegion));
      setSessionSelection({ dataRegion, storageKey });
    },
    [managedTeamRegions, queryClient, selectedRegion?.dataRegion, storageKey]
  );

  return {
    requestDataRegion: selectedRegion?.dataRegion,
    selectedRegionApiBaseUrl: selectedRegion?.apiBaseUrl,
    selectedDataRegion: selectedRegion?.dataRegion ?? null,
    setSelectedDataRegion,
  };
}

function useSessionSideEffects(args: {
  me: MeQueryResult;
  role: string | undefined;
  isTeamAdmin: boolean;
}) {
  const { user: u } = useUser();
  const avatar = u?.imageUrl;
  const identify = useTrackingIdentify();
  const intercom = useIntercom();

  const user = args.me.data?.user;
  useProductAnalyticsPreference(user?.settings.productAnalytics);
  const team = args.me.data?.team;
  const meFeatures = args.me.data?.features;

  const { data, isInitialLoading: loadingIntercomHash } = useTrpcQuery(
    'fetchIntercomHash',
    {
      tenantId: team?.id!,
    },
    {
      skip: !team?.id || !user?.id,
      refetchOnWindowFocus: false,
    }
  );

  // PRO-19466: call the team's regional API directly. The former tRPC route
  // added a Pages Function hop and a region-router forward, where Cloudflare
  // dropped requests and returned non-JSON 5xx responses.
  // A write, so it runs once per user and team. As a query it re-ran whenever
  // the team's API host or region settled after the team id. `lastSeen` is a
  // day-level signal, so it is touched on the first load of each day only.
  const api = useApiClient();
  const isTeamIdFetching = useApiIsTeamIdFetching();
  const touchedLastSeen = useRef<string | null>(null);
  useEffect(() => {
    if (!team?.id || !user?.id || isTeamIdFetching) return;
    const touchKey = `${user.id}:${team.id}`;
    if (touchedLastSeen.current === touchKey) return;
    touchedLastSeen.current = touchKey;
    const storageKey = `${user.id}_last_seen_touch`;
    const today = utc().yyyymmdd();
    if (localStorage.getItem(storageKey) === today) return;
    // PRO-19716: a transport failure rejects instead of returning `res.error`.
    // The touch stays unmarked, so the next load retries it.
    void api
      .PUT('/me', { body: { touchLastSeen: true } })
      .then((res) => {
        if (res.error) {
          captureSentryError(res.error);
          return;
        }
        localStorage.setItem(storageKey, today);
      })
      .catch(captureSentryError);
  }, [api, isTeamIdFetching, team?.id, user?.id]);

  const intercomIdentifyId = data?.intercom_user_hash;

  const isPlaywright = isPlaywrightUserEmail(user?.email || '');

  const sidekickCutoverEnabled = useMemo(
    () => !!meFeatures?.includes(featureIds.VR_SIDEKICK_CUTOVER),
    [meFeatures]
  );
  const canShowVRPilotOnboardingGuide =
    team?.isGL === true &&
    team.type === 'propertyManager' &&
    team.settings.showVRPilotGuide;

  const enableIdentify =
    !loadingIntercomHash && !args.me.isLoading && !!user && !!team;

  const { isTeamAdmin } = args;

  useEffect(() => {
    if (isPlaywright || isLocalhost) {
      intercom.hardShutdown(); // prevent showing intercom completely
      return;
    }

    if (!enableIdentify || !user?.id || !intercomIdentifyId) return;

    identify({
      canShowVRPilotOnboardingGuide,
      intercomIdentifyId,
      sidekickCutoverEnabled,
      user: {
        id: user.id,
        email: user.email,
        avatar,
        name: [user.firstName, user.lastName].filter(Boolean).join(' '),
        lastSeenAt: day().toISOString(),
        firstName: user.firstName ?? undefined,
        lastName: user.lastName ?? undefined,
        createdAt: day(user.createdAt).toISOString(),
        isPartnerAdmin: user.isPartnerAdmin,
        isVrpAdmin: user.isVrpAdmin,
        isSuperAdmin: user.isSuperAdmin,
        isTeamAdmin,
        country: undefined, // TODO: add country?
        role: user.isSuperAdmin
          ? 'super-admin'
          : user.isVrpAdmin
            ? 'vrp-admin'
            : user.isPartnerAdmin
              ? 'partner-admin'
              : isTeamAdmin
                ? 'team-admin'
                : (args.role as UserRole | undefined) || // this is the clerk role // (admin, user, owner)
                  'user',
      },
      team: team
        ? {
            id: team.id,
            name: team.name,
            isOnboarding: team.isOnboarding,
            isGL: team.isGL,
            isTest: team.isTest,
            type: team.type,
          }
        : undefined,
    });
  }, [
    enableIdentify,
    user,
    team,
    identify,
    isTeamAdmin,
    intercomIdentifyId,
    canShowVRPilotOnboardingGuide,
    isPlaywright,
    avatar,
    args.role,
    intercom.hardShutdown,
    sidekickCutoverEnabled,
  ]);
}

type SessionUserProviderProps = {
  children: ReactNode;
  me: MeQueryResult;
  isBootstrappingTeamId: boolean;
  selectedDataRegion: DataRegion | null;
  setSelectedDataRegion: (dataRegion: DataRegion) => Promise<void>;
};

const SessionUserProvider = ({
  children,
  me,
  isBootstrappingTeamId,
  selectedDataRegion,
  setSelectedDataRegion,
}: SessionUserProviderProps) => {
  const { user: u } = useUser();
  const { teamId: selectedTeamId, setTeamId } = useTeamIdContext();

  const realUserId = `${u?.publicMetadata.user_id || ''}`;
  const userId = `${u?.publicMetadata.user_id || ''}` || undefined;
  const role = (u?.publicMetadata.role as Maybe<'user' | 'owner' | 'admin'>)
    ? `${u?.publicMetadata.role}`
    : undefined;

  const meUser = me.data?.user;
  const team = me.data?.team ?? null;
  const membership = me.data?.membership ?? null;

  const isTeamAdmin =
    !!meUser &&
    (meUser.isSuperAdmin ||
      meUser.isVrpAdmin ||
      meUser.isPartnerAdmin ||
      membership?.role === 'admin');

  useSessionSideEffects({
    me,
    role,
    isTeamAdmin,
  });

  // For webextension
  useEffect(() => {
    if (u?.id) localStorage.setItem('cid', u.id);
    else localStorage.removeItem('cid');
    if (userId) localStorage.setItem('uid', userId);
    else localStorage.removeItem('uid');

    const tid = selectedTeamId || team?.id;
    if (tid) localStorage.setItem('tid', tid);
    else localStorage.removeItem('tid');
  }, [userId, u?.id, selectedTeamId, team?.id]);

  useEffect(() => {
    if (!userId || !team?.id) return;

    const storageKey = getRecentTeamIdsStorageKey(userId);
    const recentTeamIds = parseStoredRecentTeamIds(
      globalThis.localStorage?.getItem(storageKey) ?? null
    );
    const nextRecentTeamIds = upsertRecentTeamId({
      teamId: team.id,
      recentTeamIds,
    });

    globalThis.localStorage?.setItem(
      storageKey,
      JSON.stringify(nextRecentTeamIds)
    );
  }, [userId, team?.id]);

  return (
    <MeSessionContextProvider
      isBootstrappingTeamId={isBootstrappingTeamId}
      me={me}
      realUserId={realUserId}
      selectedDataRegion={selectedDataRegion}
      setSelectedDataRegion={setSelectedDataRegion}
      setTeamId={setTeamId}
    >
      <OwnerPortalContactContextProvider>
        {children}
      </OwnerPortalContactContextProvider>
    </MeSessionContextProvider>
  );
};

export type OwnerPortalAccessDeniedRenderer = (args: {
  portalName: string;
  signOut: () => void;
}) => ReactNode;

export type PortalSwitchRenderer = (args: { message: string }) => ReactNode;

const SessionProviderContent = ({
  children,
  getToken,
  refreshToken,
  renderAccessDenied,
  renderPortalSwitch,
}: {
  children: ReactNode;
  getToken: () => Promise<Maybe<string>>;
  refreshToken: () => Promise<Maybe<string>>;
  renderAccessDenied?: OwnerPortalAccessDeniedRenderer;
  renderPortalSwitch?: PortalSwitchRenderer;
}) => {
  const { user: u } = useUser();
  const { actor, signOut } = useAuth();
  const userId = `${u?.publicMetadata.user_id || ''}` || undefined;
  const portal = useOwnerPortal();
  const customPortal =
    portal && window.location.hostname === portal.hostname ? portal : null;
  const [storedTeamId, setStoredTeamId] = useLocalStorageTeamId(userId);
  const [teamSelectionUserId, setTeamSelectionUserId] = useState<string>();
  const [sti, setSti] = useQueryState('sti', parseAsString);
  const stiTeamId = sti && isUUID(sti) ? sti : undefined;
  const actorTeamId = getImpersonationActorTeamId(actor);
  const resolvedActorTeam = useRef<{
    actorTeamId: string;
    userId: string | undefined;
  } | null>(null);
  const hasResolvedActorTeam =
    resolvedActorTeam.current?.actorTeamId === actorTeamId &&
    resolvedActorTeam.current?.userId === userId;
  const reconciliationActorTeamId =
    actorTeamId && !hasResolvedActorTeam ? actorTeamId : undefined;
  const queryClient = useQueryClient();
  const { mutate: resetImpersonation } = useTrpcMutation('resetImpersonation');
  const { sendMessage } = useExtension();

  // the requested team id is a preference; the `/me` response is
  // authoritative
  const requestedTeamId =
    customPortal?.teamId ??
    resolveSessionRequestedTeamId({
      actorTeamId,
      hasResolvedActorTeam,
      stiTeamId,
      storedTeamId,
    });

  const me = useMeQuery({
    userId,
    requestedTeamId,
    preferActiveTeam:
      teamSelectionUserId !== userId &&
      !customPortal &&
      !stiTeamId &&
      !actorTeamId,
    storybookQueryKey: STORYBOOK_ME_QUERY_KEY,
  });
  const {
    requestDataRegion,
    selectedDataRegion,
    selectedRegionApiBaseUrl,
    setSelectedDataRegion,
  } = usePartnerRegionSelection({ me, userId });

  const meTeamId = me.data ? (me.data.team?.id ?? null) : undefined;
  const isExitingImpersonation = useRef(false);
  const staleRegionRefresh = useRef<Promise<void> | null>(null);
  const refreshStaleRegion = useCallback(() => {
    if (!staleRegionRefresh.current) {
      staleRegionRefresh.current = me
        .refetch()
        .then(() => undefined)
        .finally(() => {
          staleRegionRefresh.current = null;
        });
    }

    return staleRegionRefresh.current;
  }, [me]);

  useEffect(() => {
    if (customPortal || meTeamId === undefined || me.isFetching) return;

    if (reconciliationActorTeamId === meTeamId) {
      resolvedActorTeam.current = {
        actorTeamId: reconciliationActorTeamId,
        userId,
      };
    }

    const reconciliation = reconcileMeTeam({
      meTeamId,
      stiTeamId,
      actorTeamId: reconciliationActorTeamId,
    });

    if (reconciliation.exitImpersonation) {
      if (isExitingImpersonation.current) return;
      isExitingImpersonation.current = true;

      showWarnNotification({
        title: 'Team not accessible',
        message:
          'The requested team is not accessible for this preview. Exiting the impersonation session.',
      });

      if (actor?.sub && meTeamId) {
        void resetImpersonation({
          actorClerkUserId: actor.sub,
          tenantId: meTeamId,
        }).then((res: { url: string }) => signOut({ redirectUrl: res.url }));
      } else {
        void signOut();
      }

      return;
    }

    if (reconciliation.warnStiMismatch) {
      showWarnNotification({
        title: 'Team not accessible',
        message:
          'The shared link points to a team you cannot access. Continuing with your default team.',
      });
    }

    if (reconciliation.clearSti) {
      setTeamSelectionUserId(userId);
      void setSti(null);
    }

    if (
      reconciliation.persistTeamId !== (storedTeamId ?? null) ||
      requestedTeamId !== reconciliation.persistTeamId
    ) {
      // Reuse this `/me` response for the resolved team: seed the cache
      // under the resolved team's key so persisting the id does not issue a
      // second bootstrap request.
      if (reconciliation.persistTeamId && me.data) {
        queryClient.setQueryData(
          createSeededMeQueryKey(me.queryKey, reconciliation.persistTeamId),
          me.data
        );
      }

      setStoredTeamId(reconciliation.persistTeamId);

      if (reconciliation.persistTeamId) {
        sendMessage({
          message: 'team_changed',
          data: { teamId: reconciliation.persistTeamId },
        });
      }
    }
  }, [
    customPortal,
    meTeamId,
    me.isFetching,
    me.data,
    me.queryKey,
    stiTeamId,
    reconciliationActorTeamId,
    actor?.sub,
    userId,
    storedTeamId,
    requestedTeamId,
    setSti,
    setStoredTeamId,
    queryClient,
    resetImpersonation,
    sendMessage,
    signOut,
  ]);

  // This provider only mounts while signed in, so the cleanup runs on sign-out,
  // session expiry and account change. The next account must start from its
  // own `/me` team instead of the previous user's router preference.
  // biome-ignore lint/correctness/useExhaustiveDependencies: userId is the trigger for clearing the previous account's preference
  useEffect(
    () => () => {
      document.cookie = serializePortalTeamCookie(null);
    },
    [userId]
  );

  // Portal team routing (PRO-18799): keep the router's team preference current
  // and load the other app when the resolved team belongs to it. PRO-18864:
  // a cross-app switch shows one placeholder from the reload decision until
  // the target app has finished its own routing check.
  const routedTeam = me.data?.team ?? null;
  const [portalSwitchTarget, setPortalSwitchTarget] =
    useState<PortalApp | null>(() =>
      readPortalSwitchLanding(document, sessionStorage)
    );
  // PRO-19095: the page stays alive until the router answers the navigation.
  // A routing check in that window would read the reload key this page just
  // stored and report a mismatch; only the next page may judge the reload.
  const isLeavingForPortalApp = useRef(false);
  useEffect(() => {
    if (customPortal || meTeamId === undefined || me.isFetching) return;
    if (isExitingImpersonation.current || isLeavingForPortalApp.current) return;
    if (reconciliationActorTeamId && reconciliationActorTeamId !== meTeamId)
      return;

    let routedApp: ReturnType<typeof readRoutedPortalApp>;
    try {
      routedApp = readRoutedPortalApp(document);
    } catch (error) {
      captureSentryError(error);
      return;
    }
    if (!routedApp) return;

    document.cookie = serializePortalTeamCookie(meTeamId);

    const decision = resolvePortalRouting({
      routedApp,
      team: routedTeam,
      lastReloadKey: sessionStorage.getItem(PORTAL_RELOAD_STORAGE_KEY),
      pathname: window.location.pathname,
      search: window.location.search,
    });

    if (decision.action === 'none') {
      sessionStorage.removeItem(PORTAL_RELOAD_STORAGE_KEY);
      setPortalSwitchTarget(null);
      return;
    }

    if (decision.action === 'reload' || decision.action === 'start') {
      sessionStorage.setItem(PORTAL_RELOAD_STORAGE_KEY, decision.reloadKey);
      setPortalSwitchTarget(decision.expectedApp);
      isLeavingForPortalApp.current = true;
      window.location.replace(
        `${decision.reloadPath}${decision.action === 'reload' ? window.location.hash : ''}`
      );
      return;
    }

    setPortalSwitchTarget(null);

    captureSentryError(new Error('Portal router served the wrong app'), {
      routedApp,
      expectedApp: decision.expectedApp,
      teamId: meTeamId,
    });
    showWarnNotification({
      title: 'Wrong portal app',
      message:
        'This team opened in the wrong version of the portal. Reload the page, and contact support if this keeps happening.',
    });
  }, [
    customPortal,
    meTeamId,
    me.isFetching,
    routedTeam,
    reconciliationActorTeamId,
  ]);

  const { resolvedTeamId, isBootstrappingTeamId } = resolveSessionTeamId({
    userId,
    meTeamId,
    hasMeError: !!me.error,
  });

  const teamIdValue = useMemo(
    () => ({
      teamId: resolvedTeamId,
      storedTeamId,
      setTeamId: (teamId: string | null | undefined) => {
        setTeamSelectionUserId(userId);
        setStoredTeamId(teamId);
      },
    }),
    [resolvedTeamId, storedTeamId, setStoredTeamId, userId]
  );

  if (
    customPortal &&
    me.data &&
    !canAccessCustomOwnerPortal({
      expectedTeamId: customPortal.teamId,
      actualTeamId: me.data.team?.id,
      role: me.data.membership?.role,
    })
  ) {
    const signOutOfPortal = () => void signOut({ redirectUrl: '/' });
    // The application supplies its styled view; the plain fallback keeps this
    // library free of UI-kit dependencies.
    return (
      renderAccessDenied?.({
        portalName: customPortal.name,
        signOut: signOutOfPortal,
      }) ?? (
        <main>
          <h1>Owner access required</h1>
          <p>This account does not have owner access to this portal.</p>
          <button type="button" onClick={signOutOfPortal}>
            Sign out
          </button>
        </main>
      )
    );
  }

  // A failed `/me` never reaches the routing check, so its error view must
  // not stay hidden behind the placeholder.
  const portalSwitchMessage =
    portalSwitchTarget && !me.error
      ? getPortalSwitchMessage(portalSwitchTarget)
      : null;

  return (
    <teamIdContext.Provider value={teamIdValue}>
      <ApiClientProvider
        dataRegion={requestDataRegion}
        dataRegionBaseUrl={selectedRegionApiBaseUrl}
        getToken={getToken}
        onDataRegionInvalid={refreshStaleRegion}
        refreshToken={refreshToken}
        teamBaseUrlOverride={me.data?.team?.apiBaseUrl}
        teamId={resolvedTeamId}
        isTeamIdFetching={isBootstrappingTeamId || me.isFetching}
        attempts={1}
      >
        <SessionUserProvider
          me={me}
          isBootstrappingTeamId={isBootstrappingTeamId}
          selectedDataRegion={selectedDataRegion}
          setSelectedDataRegion={setSelectedDataRegion}
        >
          {portalSwitchMessage
            ? (renderPortalSwitch?.({ message: portalSwitchMessage }) ?? (
                <main>{portalSwitchMessage}</main>
              ))
            : children}
        </SessionUserProvider>
      </ApiClientProvider>
    </teamIdContext.Provider>
  );
};

export const useSessionTokenProvider = () => {
  const auth = useClerk();
  const { sendMessage } = useExtension();

  return useMemo(
    () =>
      getSharedSessionTokenProvider(auth, (token) => {
        void sendMessage({ message: 'token', data: { token } });
        if (token) {
          localStorage.setItem('at', token);
        } else {
          localStorage.removeItem('at');
        }
      }),
    [auth, auth.session?.id, sendMessage]
  );
};

export function SessionProvider({
  children,
  renderAccessDenied,
  renderPortalSwitch,
}: {
  children: ReactNode;
  renderAccessDenied?: OwnerPortalAccessDeniedRenderer;
  renderPortalSwitch?: PortalSwitchRenderer;
}) {
  const { getToken, refreshToken } = useSessionTokenProvider();

  useEffect(() => {
    void getToken().catch(captureSentryError);
  }, [getToken]);

  return (
    <ApiClientProvider
      getToken={getToken}
      refreshToken={refreshToken}
      teamId={undefined}
      isTeamIdFetching={false}
      attempts={1}
    >
      <SessionProviderContent
        getToken={getToken}
        refreshToken={refreshToken}
        renderAccessDenied={renderAccessDenied}
        renderPortalSwitch={renderPortalSwitch}
      >
        {children}
      </SessionProviderContent>
    </ApiClientProvider>
  );
}
